import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Alert,
  Avatar,
  Divider,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Stack,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  PhotoCamera as PhotoCameraIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const Profile = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Profile Information
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    phone: '',
    avatar_url: '',
  });

  // Password Change
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Preferences
  const [preferences, setPreferences] = useState({
    email_notifications: true,
    in_app_notifications: true,
    calendar_view: 'week',
    theme_preference: 'light',
    show_dashboard_sales: true,
    show_dashboard_inventory: true,
    show_dashboard_calendar: true,
    show_dashboard_leads: true,
  });

  // Activity Data
  const [activityData, setActivityData] = useState({
    last_login: '',
    account_created: '',
    recent_activities: [],
  });

  const [useMockData, setUseMockData] = useState(false);

  useEffect(() => {
    fetchProfileData();
  }, []);

  useEffect(() => {
    calculatePasswordStrength(passwordData.new_password);
  }, [passwordData.new_password]);

  const fetchProfileData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/users/profile');

      if (response.data.profile) {
        setProfileData({
          full_name: response.data.profile.full_name || '',
          email: response.data.profile.email || '',
          phone: response.data.profile.phone || '',
          avatar_url: response.data.profile.avatar_url || '',
        });
      }

      if (response.data.preferences) {
        setPreferences(response.data.preferences);
      }

      if (response.data.activity) {
        setActivityData(response.data.activity);
      }

      setUseMockData(false);
    } catch (err) {
      if (err.response?.status === 404) {
        // Use mock data for demonstration
        setUseMockData(true);
        setProfileData({
          full_name: user?.full_name || '',
          email: user?.email || '',
          phone: '',
          avatar_url: '',
        });
        setActivityData({
          last_login: new Date().toISOString(),
          account_created: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
          recent_activities: [
            { action: 'Updated sale #1024', timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() },
            { action: 'Created new invoice #2048', timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString() },
            { action: 'Added inventory items', timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString() },
            { action: 'Scheduled sale event', timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString() },
            { action: 'Converted lead to client', timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString() },
            { action: 'Generated monthly report', timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString() },
            { action: 'Updated client information', timestamp: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString() },
            { action: 'Created sale #1019', timestamp: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString() },
            { action: 'Logged in from mobile', timestamp: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString() },
            { action: 'Changed notification settings', timestamp: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString() },
          ],
        });
      } else {
        setError(err.response?.data?.error || 'Failed to load profile data');
      }
    } finally {
      setLoading(false);
    }
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/\d/.test(password)) strength += 15;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 10;
    setPasswordStrength(Math.min(100, strength));
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 40) return 'error';
    if (passwordStrength < 70) return 'warning';
    return 'success';
  };

  const getPasswordStrengthLabel = () => {
    if (passwordStrength < 40) return 'Weak';
    if (passwordStrength < 70) return 'Medium';
    return 'Strong';
  };

  const handleAvatarUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // In production, this would upload to a storage service
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileData({ ...profileData, avatar_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const saveProfileInfo = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (useMockData) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setSuccess('Profile information updated successfully');
      } else {
        await api.put('/users/profile', profileData);
        setSuccess('Profile information updated successfully');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile information');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    // Validation
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      setError('All password fields are required');
      setSaving(false);
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match');
      setSaving(false);
      return;
    }

    if (passwordData.new_password.length < 8) {
      setError('New password must be at least 8 characters long');
      setSaving(false);
      return;
    }

    try {
      if (useMockData) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setSuccess('Password changed successfully');
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: '',
        });
      } else {
        await api.put('/users/password', {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password,
        });
        setSuccess('Password changed successfully');
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: '',
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (useMockData) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setSuccess('Preferences saved successfully');
      } else {
        await api.put('/users/preferences', preferences);
        setSuccess('Preferences saved successfully');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 30) return `${diffDays} days ago`;
    return formatDate(dateString);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          My Profile
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {useMockData && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Profile API not available. Showing demo data. Changes will not be persisted.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Information Section */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Profile Information</Typography>
              </Box>

              <Grid container spacing={3}>
                {/* Avatar */}
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                  <Avatar
                    src={profileData.avatar_url}
                    sx={{ width: 120, height: 120, mb: 2, bgcolor: 'primary.main', fontSize: '3rem' }}
                  >
                    {profileData.full_name?.charAt(0) || profileData.email?.charAt(0) || 'U'}
                  </Avatar>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="avatar-upload"
                    type="file"
                    onChange={handleAvatarUpload}
                  />
                  <label htmlFor="avatar-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<PhotoCameraIcon />}
                      size="small"
                    >
                      Upload Photo
                    </Button>
                  </label>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={profileData.full_name}
                    onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    helperText="Email changes require verification"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Role"
                    value={user?.role || 'User'}
                    disabled
                    helperText="Contact admin to change role"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={saveProfileInfo}
                      disabled={saving || loading}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Password Change Section */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LockIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Change Password</Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                            edge="end"
                          >
                            {showPasswords.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="New Password"
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                            edge="end"
                          >
                            {showPasswords.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                            edge="end"
                          >
                            {showPasswords.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {passwordData.new_password && (
                  <Grid item xs={12}>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Password Strength
                        </Typography>
                        <Typography variant="caption" color={getPasswordStrengthColor()}>
                          {getPasswordStrengthLabel()}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={passwordStrength}
                        color={getPasswordStrengthColor()}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={changePassword}
                      disabled={saving || loading}
                    >
                      {saving ? 'Changing...' : 'Change Password'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Preferences Section */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Preferences</Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Notifications
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.email_notifications}
                        onChange={(e) => setPreferences({ ...preferences, email_notifications: e.target.checked })}
                      />
                    }
                    label="Email Notifications"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.in_app_notifications}
                        onChange={(e) => setPreferences({ ...preferences, in_app_notifications: e.target.checked })}
                      />
                    }
                    label="In-App Notifications"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Display Settings
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Default Calendar View</InputLabel>
                    <Select
                      value={preferences.calendar_view}
                      label="Default Calendar View"
                      onChange={(e) => setPreferences({ ...preferences, calendar_view: e.target.value })}
                    >
                      <MenuItem value="day">Day</MenuItem>
                      <MenuItem value="week">Week</MenuItem>
                      <MenuItem value="month">Month</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Theme Preference</InputLabel>
                    <Select
                      value={preferences.theme_preference}
                      label="Theme Preference"
                      onChange={(e) => setPreferences({ ...preferences, theme_preference: e.target.value })}
                    >
                      <MenuItem value="light">Light</MenuItem>
                      <MenuItem value="dark">Dark</MenuItem>
                      <MenuItem value="auto">Auto</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Dashboard Widgets
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.show_dashboard_sales}
                        onChange={(e) => setPreferences({ ...preferences, show_dashboard_sales: e.target.checked })}
                      />
                    }
                    label="Show Sales Widget"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.show_dashboard_inventory}
                        onChange={(e) => setPreferences({ ...preferences, show_dashboard_inventory: e.target.checked })}
                      />
                    }
                    label="Show Inventory Widget"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.show_dashboard_calendar}
                        onChange={(e) => setPreferences({ ...preferences, show_dashboard_calendar: e.target.checked })}
                      />
                    }
                    label="Show Calendar Widget"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.show_dashboard_leads}
                        onChange={(e) => setPreferences({ ...preferences, show_dashboard_leads: e.target.checked })}
                      />
                    }
                    label="Show Leads Widget"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={savePreferences}
                      disabled={saving || loading}
                    >
                      {saving ? 'Saving...' : 'Save Preferences'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Activity Section */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Activity</Typography>
              </Box>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Last Login
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(activityData.last_login)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Account Created
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(activityData.account_created)}
                  </Typography>
                </Box>

                <Divider />

                <Typography variant="subtitle2" fontWeight="bold">
                  Recent Activity
                </Typography>

                <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {activityData.recent_activities.map((activity, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemText
                        primary={activity.action}
                        secondary={getRelativeTime(activity.timestamp)}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                  {activityData.recent_activities.length === 0 && (
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary="No recent activity"
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile;
